import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  DataTable, DownloadButton, InfoHint, Panel, SegmentedControl, type DataTableColumn,
} from '../../../../ui-kit';
import type { PlannerCampaign, PlannerDay } from '../../../../types/plannerDashboard';
import { oneDecimal, percent, whole } from '../plannerFormat';

type Row = PlannerCampaign & { order: number };

function Heading({ label, info }: { label: string; info: React.ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {label}
      <InfoHint title={info} label={`About ${label.toLowerCase()}`} />
    </Box>
  );
}

interface CampaignsPanelProps {
  days: PlannerDay[];
  dow: number;
  onDowChange: (dow: number) => void;
  target: number;
  loading?: boolean;
  onDownload: () => void;
  downloading?: boolean;
  problem?: string | null;
}

/**
 * One day's campaigns in the order the plan uses them: where that day's
 * leads come from, and the point at which weaker campaigns pull conversion
 * down.
 */
export default function CampaignsPanel({
  days, dow, onDowChange, target, loading, onDownload, downloading, problem,
}: CampaignsPanelProps) {
  const day = days.find((d) => d.dow === dow) ?? days[0];
  const rows: Row[] = (day?.campaigns ?? []).map((c, i) => ({ ...c, order: i + 1 }));

  const columns: DataTableColumn<Row>[] = [
    // Rank and campaign name stay put while the figures scroll past them.
    { key: 'order', header: '#', width: 44, sticky: true, muted: true, render: (r) => r.order },
    {
      key: 'campaign',
      header: 'Campaign',
      width: 260,
      sticky: true,
      render: (r) => (
        <Box sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <span>{r.campaign}</span>
          {r.few_leads && (
            <InfoHint
              tone="warning"
              label="Few Sales Leads"
              title="Too few Sales Leads over the date range to trust this campaign's own conversion, so it is used after the others."
            />
          )}
        </Box>
      ),
    },
    { key: 'channel', header: 'Channel', muted: true, render: (r) => r.channel },
    {
      key: 'leads',
      header: (
        <Heading
          label="Leads"
          info="How many new leads this campaign brings in on this day of the week, on average: every lead, MQL or not. For Monday: all its Monday leads in the date range ÷ the number of Mondays."
        />
      ),
      numeric: true,
      render: (r) => oneDecimal(r.leads_per_day),
    },
    {
      key: 'mqls',
      header: (
        <Heading
          label="MQLs"
          info="How many of those leads are marked MQL, on average, We're Sorry and Alt Mon included."
        />
      ),
      numeric: true,
      render: (r) => oneDecimal(r.mqls_per_day),
    },
    {
      key: 'sales_leads',
      header: (
        <Heading
          label="Sales Leads"
          info="How many of those leads are Sales Leads, on average: marked MQL, and not We're Sorry or Alt Mon. Agents are given Sales Leads, so contacted, conversion and used count only these."
        />
      ),
      numeric: true,
      render: (r) => oneDecimal(r.sales_leads_per_day),
    },
    {
      key: 'reached',
      header: (
        <Heading
          label="Contacted"
          info="Of those Sales Leads, how many an agent actually spoke with — a call that lasted 90 seconds or more. A daily average."
        />
      ),
      numeric: true,
      render: (r) => oneDecimal(r.reached_per_day),
    },
    {
      key: 'conversion',
      header: (
        <Heading
          label="Conversion"
          info="Of this campaign's Sales Leads that an agent reached, the share that sent an application to the carrier (CAS), on this day of the week. The list is sorted by it."
        />
      ),
      numeric: true,
      render: (r) => percent(r.conversion),
    },
    {
      key: 'used',
      header: (
        <Heading
          label="Used"
          info={
            <>
              How many of this campaign&apos;s Sales Leads a day go to agents under your plan.
              <br /><br />
              Sales Leads are handed out from the top of this list down until the day&apos;s Sales Leads needed are covered.
              Campaigns above that point give all their Sales Leads, the one where it is reached gives only what is still
              needed, and the ones below give none — &ldquo;Not needed&rdquo;.
            </>
          }
        />
      ),
      numeric: true,
      render: (r) =>
        r.sales_leads_used > 0 ? oneDecimal(r.sales_leads_used) : <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>Not needed</Box>,
    },
    {
      key: 'so_far',
      header: (
        <Heading
          label="So far"
          info={
            <>
              The conversion of all the Sales Leads used from the top of the list down to this row, taken together:
              their applications ÷ their Sales Leads contacted.
              <br /><br />
              It falls as weaker campaigns are added. The figure on the last used row is the day&apos;s conversion,
              and a figure in red is below your target — the point where giving agents more Sales Leads starts to cost
              conversion.
            </>
          }
        />
      ),
      numeric: true,
      render: (r) =>
        r.conversion_so_far === null ? '' : (
          <Box
            component="span"
            sx={(t) => ({
              fontWeight: 600,
              color: Math.round(r.conversion_so_far! * 10) / 10 >= target ? t.tokens.text.primary : t.tokens.semantic.danger,
            })}
          >
            {percent(r.conversion_so_far)}
          </Box>
        ),
    },
  ];

  const table = (fill: boolean) => (
    <DataTable
      ariaLabel={`Campaigns on ${day?.name ?? ''}`}
      columns={columns}
      groups={[{ key: 'rows', title: '', rows }]}
      getRowKey={(r) => `${r.order}-${r.channel}-${r.campaign}`}
      collapsibleGroups={false}
      maxHeight={fill ? undefined : 560}
      fill={fill}
      emptyMessage="No leads on this day in the date range."
    />
  );

  const subtitle = day
    ? day.short_by >= 0.5
      ? `Every campaign is used and ${day.name} is still ${whole(day.short_by)} Sales Leads short.`
      : `Sales Leads are taken from the top until ${day.name}'s ${whole(day.sales_leads_needed)} are covered.`
    : undefined;

  return (
    <Panel
      title={day ? `Campaigns on ${day.name}` : 'Campaigns'}
      subtitle={subtitle}
      tools={
        <>
          <SegmentedControl
            ariaLabel="Day of the week"
            value={String(dow)}
            onChange={(v) => onDowChange(Number(v))}
            options={days.map((d) => ({ value: String(d.dow), label: d.short_name }))}
          />
        </>
      }
      actions={
        <>
          <DownloadButton
            formats={[{ key: 'xlsx', label: 'Excel (.xlsx)', description: 'Every day\'s campaigns', onDownload }]}
            tooltip="Download every day's campaigns as Excel"
            loading={downloading}
            disabled={loading}
          />
        </>
      }
      loading={loading}
      loadingHeight={360}
      flush
      footer={
        problem ?? (
          <Typography variant="caption" component="span">
            Best-converting first. Campaigns marked with a warning have too few Sales Leads to trust their own conversion, so they go last.
          </Typography>
        )
      }
      expanded={{ content: table(true) }}
    >
      {table(false)}
    </Panel>
  );
}
